import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Typography,
  Box,
  styled,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import ToastSnackbar, { SnackbarHandle } from "../../common/ToastNotification";
import HomeIcon from "@mui/icons-material/Home";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { useApi } from "../../store/UserLoginContext";
import { useForm } from "react-hook-form";
import { GetVehicles, GetStops } from "../../api/Control-Settings/manage-transport";
import { Get as GetClass } from "../../api/Control-Settings/manage-class";
import ControlledSelect from "../../common/ControlledComponents/ControlledSelect";
import { GetReportTransport } from "../../api/Report-Center/transport-details";
import ReportTransport from "../../templates/ReportTransport";
import { useReactToPrint } from "react-to-print";

const MyCustomButton = styled(Button)(({ theme }) => ({
  fontFamily: "Motiva Sans Bold",
  fontSize: "0.80rem",
  fontWeight: "700",
  border: "1px solid #edf3f5",
  borderRadius: "4px",
  background: "#2e186a",
  boxShadow: "0 16px 30px rgb(23 31 114 / 20%)",
  marginTop: "0rem",
  "&:hover": {
    color: "#fff",
    border: "1px solid rgb(255, 130, 92)",
    backgroundColor: "rgb(255, 130, 92)",
  },
}));

const TransportReport = () => {
  const { currentSessionAY } = useApi();
  const snackbarRef = React.useRef<SnackbarHandle>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicleRecords, setVehicleRecords] = useState([]);
  const [stopsRecords, setStopsRecords] = useState([]);
  const [classRecords, setClassRecords] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [readyToPrint, setReadyToPrint] = useState(false);
  const containerRef = useRef();
  const [printData, setPrintData] = useState<any>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [vehicle_records, stops_records, class_records] = await Promise.all([
          GetVehicles(),
          GetStops(),
          GetClass(),
        ]);
  
        if (vehicle_records && vehicle_records.result.documents?.length > 0) {
          const vehicle_records_arr = vehicle_records.result.documents;
          vehicle_records_arr.sort((a:any, b: any) => a.vehicle_no.localeCompare(b.vehicle_no));
          setVehicleRecords(vehicle_records_arr);
        }
        if (stops_records && stops_records.result.documents?.length > 0) {
          const stops_records_arr = stops_records.result.documents;
          stops_records_arr.sort((a: any, b: any) => a.name.localeCompare(b.name));
          setStopsRecords(stops_records_arr);
        }
        if (class_records && class_records.result.documents?.length > 0) {
          setClassRecords(class_records.result.documents);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        snackbarRef.current?.showSnackbar(`Error fetching data`, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors },
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      class_id: "",
      vehicle_id: "",
      stop_name: ""
    },
    mode: "onTouched",
  });

  const handlePrint = useReactToPrint({
    documentTitle: "",
    onAfterPrint: () => setReadyToPrint(false),
  }) as (content: () => HTMLElement | null) => () => void;

  const handleFormSubmit = async (data: any) => {
    try{
      setFetching(true);
      console.log(data);

      //if no vehicle and stop is selected
      if(!data.vehicle_id && !data.stop_name){
        snackbarRef.current?.showSnackbar(`Please select at least Vehicle or Stop`, "warning")
        return;
      }

      const payload = {
        academic_year_id: currentSessionAY,
        class_id: data.class_id,
        vehicle_id: data.vehicle_id,
        stop_id: data.stop_name
      }
      
      const response = await GetReportTransport(payload);
      console.log(response);

      if (response.total_records === 0) {
        snackbarRef.current?.showSnackbar(`No Records Found`, "info")
        return;
      }

      setPrintData(response.result);
      setReadyToPrint(true);

      setTimeout(() => {
        try {
          handlePrint(() => containerRef.current!)();
        } catch (error) {
          console.log("Handling print function call error.");
          console.log(error);
        }
      }, 400);


    } catch (error) {
      console.log(error);
      snackbarRef.current?.showSnackbar(`Some Error occured`, "error")
    } finally {
      setFetching(false);
    }
  }

  const onResetHandler = () => {
    reset({
      vehicle_id: "",
      stop_name: "",
      class_id: "",
    })
  }

  return (
    <>
      <ToastSnackbar ref={snackbarRef} />
      {readyToPrint && (
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            top: "0px",
          }}
        >
          {/* @ts-ignore */}
          <ReportTransport ref={containerRef} data={printData} />
        </div>
      )}
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
        p={2}
        pb={0}
        gap={1}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"flex-start"}
          p={2}
          pb={0}
          gap={1}
        >
          <MyCustomButton
            variant="contained"
            startIcon={<ArrowLeftIcon />}
            onClick={() => navigate("..")}
          >
            Go Back
          </MyCustomButton>
          <MyCustomButton
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/home")}
          >
            Home
          </MyCustomButton>
        </Box>
      </Box>

      <Box
        display={"flex"}
        flexDirection={"column"}
        p={2}
        pt={0}
        height="auto"
        width="auto"
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          p={2}
          pt={0}
          height="auto"
          justifyContent={"center"}
          alignItems={"center"}
          width={"90vw"}
        >
          <Typography variant="h6" alignSelf={"center"}>
            <strong>Select Vehicle Or Stop</strong>
          </Typography>
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-evenly"}
            gap={3}
            width="auto"
            mt={4}
          >
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100px"
              >
                <CircularProgress />
              </Box>
            ) : (
              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                onReset={onResetHandler}
              >
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-evenly"}
                  gap={3}
                  width={"70vw"}
                >
                  <ControlledSelect
                    name={`vehicle_id`}
                    control={control}
                    errors={errors}
                    label="Vehicle No."
                    options={vehicleRecords.map((item:any) => ({
                      value: item.vehicle_no,
                      label: item.vehicle_no
                    }))}
                    sx={{width: "30%"}}
                  />
                  <ControlledSelect
                    name={`stop_name`}
                    control={control}
                    errors={errors}
                    label="Stops"
                    options={stopsRecords.map((item:any) => ({
                      value: item.name,
                      label: item.name
                    }))}
                    sx={{width: "30%"}}
                  />
                  <ControlledSelect
                    name={`class_id`}
                    control={control}
                    errors={errors}
                    label="Class"
                    rules={{ required : "Required"}}
                    options={classRecords.map((item:any) => ({
                      value: item.class_id,
                      label: item.name
                    }))}
                    sx={{width: "30%"}}
                  />
                  <MyCustomButton
                    variant="contained"
                    type="submit"
                    startIcon={fetching ? <CircularProgress size={20}/> : null}
                    disabled={fetching}
                    sx={{
                      alignSelf:"center",
                      height:"70%",
                      width:"20%",
                    }}
                  >
                    {fetching ? "" : "Fetch Report"}
                  </MyCustomButton>
                  <MyCustomButton
                    variant="contained"
                    type="reset"
                    sx={{
                      alignSelf: "center",
                      height: "70%",
                      width: "20%",
                    }}
                  >
                    Clear
                  </MyCustomButton>
                </Box>
              </form>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default TransportReport;